import { types, getSnapshot } from 'mobx-state-tree';
import { v4 as uuid } from 'uuid';

import { partiallyUpdateModel, removeEmptyProperties } from '../definitions/common';
import SCENARIOS from '../definitions/Scenarios';
import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import Event from './Event';
import Contact from './Contact';
import CardPreference from './CardPreference';
import HostedApplication from './HostedApplication';
import CreditReport from './CreditReport';
import Metadata from './Metadata';


const Journey = types
    .model('Journey', {
        id: types.optional(types.identifier, uuid),
        contact: types.optional(Contact, {}),
        cardPreference: types.optional(CardPreference, {}),
        creditReport: types.optional(CreditReport, {}),
        application: types.optional(HostedApplication, {}),

        events: types.optional(types.array(Event), []),
        metadata: types.optional(Metadata, {}),
    })
    .actions(self => ({
        update(values) {
            if (values.events) {
                throw new Error('You cannot mutate events, they are read-only.');
            }

            // ensure previous creditReport fields are reset to prevent bail before new document is generated
            if (values?.creditReport?.isRequesting) {
                self.creditReport.attempts = 0;
                self.creditReport.creditBureau = undefined;
                self.creditReport.stage = undefined;
                self.creditReport.creditReportId = undefined;
            }

            // ensure SIN has string value to enable encryption
            if (self.contact && self.contact.sin == null) {
                self.contact.sin = '';
            }

            // ensure error and redirect are always reset because BE will never need to receive them
            if (values.application?.isSubmitting) {
                self.application.responseStatus = undefined;
                self.application.responseMessage = undefined;
                self.application.redirectUrl = undefined;
            }

            // partiallyUpdateModel merge causes issues, therefore we will reset a few values if required
            const nextScenario = values?.cardPreference?.scenario;
            if (isScenarioRewards(self.cardPreference?.scenario)) {
                if (isScenarioLowInterest(nextScenario)) {
                    // we're switching to Low Interest: clear out Rewards values
                    self.cardPreference.rewardType = null;
                    self.cardPreference.monthlySpending = null;
                    self.cardPreference.monthlySpendingAverage = null;
                    self.cardPreference.features = DEFAULT_CARD_PREFERENCES.features;
                } else if (values.cardPreference?.features) {
                    // we're not switching scenario but updating features: clear old features array
                    self.cardPreference.features = DEFAULT_CARD_PREFERENCES.features;
                }
            } else if (isScenarioRewards(nextScenario)) {
                // we're switching to Rewards: clear out Low Interest values
                self.cardPreference.isHighBalance = null;
                self.cardPreference.transferAmount = DEFAULT_CARD_PREFERENCES.balanceTransferAmount;
                self.cardPreference.creditBalance = DEFAULT_CARD_PREFERENCES.lowInterestMonthlyBalance;
            }

            partiallyUpdateModel(self, values);
        },
        trackEvent({ type, details, metadata, contact }) {
            if (!type) {
                throw new Error('Must specify an event type');
            }

            contact = contact ?? removeEmptyProperties(getSnapshot(self.contact));
            // ensure we do not store null SIN on contact
            if (contact.sin == null) {
                this.update({});
            }

            // ensure we not store any SIN on event
            contact.sin = '';

            self.events.push({
                type,
                timestamp: new Date().toISOString(),
                details: getSnapshot(details),
                cardPreference: removeEmptyProperties(getSnapshot(self.cardPreference)),
                contact,
                metadata,
            });
        },
    }));

function isScenarioRewards(scenario) {
    return scenario === SCENARIOS.REWARDS;
}

function isScenarioLowInterest(scenario) {
    return scenario === SCENARIOS.LOW_INTEREST || scenario === SCENARIOS.BALANCE_TRANSFER;
}

export default Journey;
